import {
  Bell,
  Check,
  CheckCheck,
  Filter,
  Search,
  Settings,
  Trash2,
  Undo2,
  X,
} from 'lucide-react';

import { useCallback, useEffect, useState } from 'react';

import { useTranslation } from '@splitfiesta/i18n';
import type { NotificationResponseDto } from '@splitfiesta/shared';
import { Button, Card, Input } from '@splitfiesta/ui';

import { withAuth } from '@/components/auth';
import { AppLayout } from '@/components/layout/AppLayout';
import NotificationItem from '@/components/notifications/NotificationItem';
import { NotificationsPageSkeleton } from '@/components/skeleton';
import { LinkButton } from '@/components/ui/LinkButton';

import {
  useDeleteNotificationsMutation,
  useMarkNotificationsAsReadMutation,
  useMarkNotificationsAsSeenMutation,
  useNotificationsInfiniteQuery,
} from '@/hooks/queries/useNotifications';
import { useSkeletonDebug } from '@/hooks/useSkeletonDebug';

interface FilterState {
  unreadOnly: boolean;
  search: string;
  type: 'all' | 'expense' | 'group' | 'payment' | 'system';
}

/**
 * Container component for notifications page
 * Following the 2-strategy state management pattern
 */
function NotificationsPage() {
  const { ready } = useTranslation('notifications');
  const { forceSkeleton } = useSkeletonDebug();

  // Local UI state
  const [filters, setFilters] = useState<FilterState>({
    unreadOnly: false,
    search: '',
    type: 'all',
  });
  const [showFilters, setShowFilters] = useState(false);

  // React Query hooks - pass all filters to backend
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    refetch,
  } = useNotificationsInfiniteQuery({
    limit: 25,
    unreadOnly: filters.unreadOnly,
    type: filters.type !== 'all' ? filters.type : undefined,
    search: filters.search || undefined,
  });

  const markAsReadMutation = useMarkNotificationsAsReadMutation();
  const markAsSeen = useMarkNotificationsAsSeenMutation();
  const deleteMutation = useDeleteNotificationsMutation();

  // Mark all notifications as seen when page opens (Facebook-style UX)
  // This clears the badge count immediately, same as dropdown behavior
  useEffect(() => {
    markAsSeen.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Extract notifications from infinite query pages with page index for unique keys
  // Handle both new cursor-based and legacy response formats
  const allNotifications =
    data?.pages.flatMap((page, pageIndex) => {
      // Type-safe access to page data
      const pageData = page.data as { items?: NotificationResponseDto[] };
      const items = pageData?.items || [];
      return items.map((item: NotificationResponseDto) => ({
        ...item,
        _pageIndex: pageIndex,
      }));
    }) || [];

  const unreadCount = (() => {
    const firstPage = data?.pages[0]?.data;
    if (firstPage && 'unreadCount' in firstPage) {
      return firstPage.unreadCount;
    }
    return 0;
  })();

  // No client-side filtering - all filtering happens server-side
  const notifications = allNotifications;

  // Handlers
  const handleMarkAllAsRead = useCallback(async () => {
    await markAsReadMutation.mutateAsync({ markAll: true });
  }, [markAsReadMutation]);

  const handleDeleteOne = useCallback(
    async (id: string) => {
      await deleteMutation.mutateAsync({ notificationIds: [id] });
    },
    [deleteMutation]
  );

  // Per-row toggle: flip whichever direction the current state isn't.
  // Reuses the same mutation as mark-all, just with the `read` flag
  // the API accepts.
  const handleToggleRead = useCallback(
    async (id: string, currentlyRead: boolean) => {
      await markAsReadMutation.mutateAsync({
        notificationIds: [id],
        read: !currentlyRead,
      });
    },
    [markAsReadMutation]
  );

  // Show skeleton while loading or when forced via debug mode
  if (!ready || isLoading || forceSkeleton) {
    return (
      <AppLayout>
        <NotificationsPageSkeleton />
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <NotificationsPagePresentation
        notifications={notifications}
        unreadCount={unreadCount}
        filters={filters}
        showFilters={showFilters}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        isError={isError}
        isMarkingRead={markAsReadMutation.isPending}
        onToggleFilters={() => setShowFilters(!showFilters)}
        onFiltersChange={setFilters}
        onMarkAllAsRead={handleMarkAllAsRead}
        onDeleteOne={handleDeleteOne}
        onToggleRead={handleToggleRead}
        onLoadMore={fetchNextPage}
        onRefresh={refetch}
      />
    </AppLayout>
  );
}

/**
 * Presentation component for notifications page UI
 */
interface NotificationsPagePresentationProps {
  notifications: (NotificationResponseDto & { _pageIndex?: number })[];
  unreadCount: number;
  filters: FilterState;
  showFilters: boolean;
  hasNextPage?: boolean;
  isFetchingNextPage: boolean;
  isError: boolean;
  isMarkingRead: boolean;
  onToggleFilters: () => void;
  onFiltersChange: (filters: FilterState) => void;
  onMarkAllAsRead: () => void;
  /** Destructive — single delete invoked from per-row trash button. */
  onDeleteOne: (id: string) => void;
  /** Per-row read/unread toggle. Flips whichever direction the row
      isn't currently — same shape as the mobile swipe primary. */
  onToggleRead: (id: string, currentlyRead: boolean) => void;
  onLoadMore: () => void;
  onRefresh: () => void;
}

function NotificationsPagePresentation({
  notifications,
  unreadCount,
  filters,
  showFilters,
  hasNextPage,
  isFetchingNextPage,
  isError,
  isMarkingRead,
  onToggleFilters,
  onFiltersChange,
  onMarkAllAsRead,
  onDeleteOne,
  onToggleRead,
  onLoadMore,
  onRefresh,
}: NotificationsPagePresentationProps) {
  const { t } = useTranslation('notifications');

  // Check if any filters are active
  const hasActiveFilters =
    filters.search || filters.unreadOnly || filters.type !== 'all';

  return (
    <div className="max-w-2xl mx-auto">
      {/* Simplified Header with Quick Actions */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              {t('page_title')}
            </h1>
            {unreadCount > 0 && (
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                {t('unread_count', { count: unreadCount })}
              </p>
            )}
          </div>

          {/* Quick Actions */}
          <div className="flex flex-col gap-2 w-full sm:w-auto items-center sm:items-end">
            <LinkButton
              href="/settings"
              size="sm"
              className="!py-1.5 !px-3"
              data-testid="notification-settings"
            >
              <Settings className="h-4 w-4 mr-1" />
              {t('settings')}
            </LinkButton>
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              {unreadCount > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onMarkAllAsRead}
                  disabled={isMarkingRead}
                  leftIcon={<CheckCheck className="h-4 w-4" />}
                  className="flex-1 sm:flex-none"
                  data-testid="mark-all-read-header"
                >
                  {t('mark_all_read')}
                </Button>
              )}
              <Button
                variant={
                  hasActiveFilters || showFilters ? 'primary' : 'outline-solid'
                }
                size="sm"
                onClick={onToggleFilters}
                leftIcon={
                  showFilters ? (
                    <X className="h-4 w-4" />
                  ) : (
                    <Filter className="h-4 w-4" />
                  )
                }
                className="relative flex-1 sm:flex-none"
                data-testid="toggle-filters"
              >
                {showFilters ? t('hide_filters') : t('show_filters')}
                {hasActiveFilters && !showFilters && (
                  <span className="absolute -top-1 -right-1 h-2 w-2 bg-orange-500 rounded-full" />
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Collapsible Filter Panel */}
      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out ${
          showFilters ? 'max-h-96 opacity-100 mb-6' : 'max-h-0 opacity-0'
        }`}
      >
        <Card className="border-2 border-orange-200 dark:border-orange-900/50">
          <div className="flex flex-col gap-4">
            {/* Search Bar */}
            <div className="flex-1">
              <Input
                type="text"
                placeholder={t('search_placeholder')}
                value={filters.search}
                onChange={e =>
                  onFiltersChange({ ...filters, search: e.target.value })
                }
                icon={<Search className="w-5 h-5 text-gray-400" />}
                className="w-full"
                inputClassName="text-gray-900"
                data-testid="notifications-search"
              />
            </div>

            {/* Filter Options */}
            <div className="flex flex-col sm:flex-row gap-4 pb-2">
              {/* Unread Only Toggle */}
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filters.unreadOnly}
                  onChange={e =>
                    onFiltersChange({
                      ...filters,
                      unreadOnly: e.target.checked,
                    })
                  }
                  className="rounded-sm border-gray-300 dark:border-gray-600 text-orange-500 focus:ring-orange-400"
                  data-testid="unread-only-filter"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">
                  {t('show_unread_only')}
                </span>
              </label>

              {/* Type Filter */}
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">
                  {t('type')}:
                </label>
                <select
                  value={filters.type}
                  onChange={e =>
                    onFiltersChange({
                      ...filters,
                      type: e.target.value as FilterState['type'],
                    })
                  }
                  className="px-3 py-1.5 pr-7 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-hidden focus:ring-2 focus:ring-orange-400 focus:border-orange-400 cursor-pointer appearance-none bg-[url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20width%3D%2710%27%20height%3D%276%27%20viewBox%3D%270%200%2010%206%27%20xmlns%3D%27http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%27%3E%3Cpath%20d%3D%27M1%201l4%204%204-4%27%20stroke%3D%27%239CA3AF%27%20stroke-width%3D%271.5%27%20fill%3D%27none%27%20fill-rule%3D%27evenodd%27%2F%3E%3C%2Fsvg%3E')] bg-size-[10px_6px] bg-position-[right_0.75rem_center] bg-no-repeat"
                  data-testid="type-filter"
                >
                  <option value="all">{t('all_types')}</option>
                  <option value="expense">{t('type_expense')}</option>
                  <option value="group">{t('type_group')}</option>
                  <option value="payment">{t('type_payment')}</option>
                  <option value="system">{t('type_system')}</option>
                </select>
              </div>

              {/* Clear Filters */}
              {hasActiveFilters && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    onFiltersChange({
                      search: '',
                      unreadOnly: false,
                      type: 'all',
                    })
                  }
                  className="ml-auto text-orange-600 hover:text-orange-700 dark:text-orange-400 dark:hover:text-orange-300"
                  data-testid="clear-filters"
                >
                  {t('clear_filters')}
                </Button>
              )}
            </div>
          </div>
        </Card>
      </div>

      {/* Notifications List */}
      <Card>
        {isError ? (
          <div className="p-8 text-center">
            <p className="text-red-500 mb-4">{t('error_loading')}</p>
            <Button onClick={onRefresh} variant="outline" size="sm">
              {t('retry')}
            </Button>
          </div>
        ) : notifications.length === 0 ? (
          <div className="p-12 text-center">
            <Bell className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-400 text-lg">
              {filters.search || filters.unreadOnly || filters.type !== 'all'
                ? t('no_notifications_filtered')
                : t('no_notifications')}
            </p>
          </div>
        ) : (
          <>
            {/* Notification Items */}
            <div>
              {notifications.map((notification, index) => (
                <div
                  key={`${notification._pageIndex ?? 0}-${notification.id}`}
                  className={`flex items-start gap-3 -mx-6 px-6 py-4 transition-colors ${
                    !notification.isRead
                      ? 'bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30'
                      : 'hover:bg-gray-50 dark:hover:bg-gray-800/50'
                  } ${
                    index !== notifications.length - 1
                      ? 'border-b border-gray-200 dark:border-gray-700'
                      : ''
                  }`}
                >
                  {/* Left-side unread indicator. Reserves a fixed
                      slot so read and unread rows stay vertically
                      aligned (Gmail-style). Non-interactive — the
                      explicit Check/Undo2 toggle on the right side
                      is what flips read state. */}
                  <div
                    className="shrink-0 mt-5 flex h-5 w-5 items-center justify-center"
                    aria-hidden="true"
                  >
                    {!notification.isRead && (
                      <span className="h-2 w-2 rounded-full bg-blue-500" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <NotificationItem
                      notification={notification}
                      showGroupName={true}
                      compact={true}
                    />
                  </div>
                  <div className="shrink-0 mt-5 flex items-center gap-2">
                    {/* Per-row read/unread toggle. Icon flips so the
                        affordance always shows what tapping will *do*
                        rather than the current state. */}
                    <button
                      type="button"
                      onClick={() =>
                        onToggleRead(notification.id, notification.isRead)
                      }
                      className="rounded p-1 text-gray-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:text-primary-400 dark:hover:bg-primary-500/10"
                      aria-label={
                        notification.isRead
                          ? t('mark_as_unread')
                          : t('mark_as_read')
                      }
                      data-testid={`toggle-read-${notification.id}`}
                    >
                      {notification.isRead ? (
                        <Undo2 className="h-4 w-4" />
                      ) : (
                        <Check className="h-4 w-4" />
                      )}
                    </button>
                    {/* Per-row delete. Wrapped in a confirm to match
                        the destructive contract elsewhere on web —
                        mobile has its own Alert.alert confirm. */}
                    <button
                      type="button"
                      onClick={() => {
                        if (window.confirm(t('deleteOneConfirm'))) {
                          onDeleteOne(notification.id);
                        }
                      }}
                      className="rounded p-1 text-gray-400 hover:text-error-600 hover:bg-error-50 dark:hover:text-error-500 dark:hover:bg-error-500/10"
                      aria-label={t('delete')}
                      data-testid={`delete-notification-${notification.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Load More Button */}
            {hasNextPage && (
              <div className="-mx-6 px-6 py-4 flex justify-center border-t border-gray-200 dark:border-gray-700">
                <LinkButton
                  onClick={onLoadMore}
                  disabled={isFetchingNextPage}
                  data-testid="load-more-notifications"
                >
                  {isFetchingNextPage ? t('loading_more') : t('load_more')}
                </LinkButton>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}

export default withAuth(NotificationsPage);
